import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ClipboardList, FileText, CreditCard, Settings as SettingsIcon,
  LogOut, Menu, X, Sparkles,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/assessment", label: "Assessment", icon: ClipboardList, testid: "nav-assessment" },
  { to: "/reports", label: "My Reports", icon: FileText, testid: "nav-reports" },
  { to: "/pricing", label: "Pricing", icon: CreditCard, testid: "nav-pricing" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const KIT_NAV = { to: "/success-kit", label: "Success Kit", icon: Sparkles, testid: "nav-success-kit" };

function NavItems({ onNavigate, items }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={item.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 ${
              isActive ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <item.icon className="h-4 w-4" strokeWidth={2} />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "U").slice(0, 1).toUpperCase();
  const navItems = user?.plan === "success_kit"
    ? [...NAV.slice(0, 3), KIT_NAV, ...NAV.slice(3)]
    : NAV;

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar */}
      <aside className="hidden md:flex w-[240px] flex-col border-r border-slate-200 px-4 py-6 fixed h-screen">
        <div className="flex items-center gap-2 px-2 mb-8">
          <img src="/logo-full.png" alt="CanHelp Move" className="h-8 w-auto" />
        </div>
        <NavItems items={navItems} />
        <div className="mt-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-slate-100 transition-colors">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white overflow-hidden">
                  {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-medium">{user?.name || "User"}</div>
                  <div className="truncate text-xs text-slate-500">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <SettingsIcon className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 h-14">
        <div className="flex items-center gap-2">
          <img src="/logo-full.png" alt="CanHelp Move" className="h-6 w-auto" />
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)}>
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden fixed top-14 inset-x-0 z-30 border-b border-slate-200 bg-white p-4">
          <NavItems items={navItems} onNavigate={() => setMobileOpen(false)} />
          <button onClick={handleLogout} className="mt-3 flex items-center gap-3 px-3 py-2 text-sm text-slate-600">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 md:ml-[240px] pt-14 md:pt-0">
        {children}
      </main>
    </div>
  );
}
